"use client";

import { memo, useState, useCallback, useRef, useEffect } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useAuth } from "@/contexts/auth-context";
import { useAccountModal } from "@/contexts/account-modal-context";
import { generateExercise } from "@/lib/api/api-client";
import { createSongFromRawAnalysis } from "@/lib/music/song-factory";
import { SongControler } from "@/lib/track/controler/song-controler";
import { useGenerationHistory } from "@/hooks/use-generation-history";
import { useModifierKey } from "@/hooks/use-modifier-key";
import type { GenerationHistoryItem } from "@/hooks/use-generation-history";
import type { AnalysisKey, AnalysisStatistics } from "@/lib/music/analyzer-types";

import { PromptSuggestions } from "../../molecules/prompt-suggestions";
import { GenerationParams } from "../../molecules/generation-params";
import { GenerationHistory } from "../../molecules/generation-history";
import { SparklesIcon, LoaderIcon, CheckIcon } from "../../atoms/icons";

type PanelStatus = "idle" | "generating" | "success" | "error";

/**
 * Largeur de la feuille, plafonnée pour laisser une gouttière de 48 px.
 *
 * Le panneau est monté en absolu au-dessus du roll : à 390 px de large, 380 px
 * fixes le recouvrent entièrement et il ne reste plus rien à toucher pour le
 * refermer. 48 px, c'est la cible tactile minimale — juste ce qu'il faut pour
 * atteindre le scrim armé par `StudioFrame`.
 *
 * La même expression sert au conteneur animé et à la feuille : s'ils
 * divergeaient, la translation d'ouverture laisserait un décalage ou un
 * liseré à droite.
 */
const PANEL_WIDTH = "min(380px, calc(100vw - 48px))";

interface GeneratePanelProps {
  open: boolean;
  onClose: () => void;
  onSongLoaded?: (songControler: SongControler) => void;
  /** Info from the currently loaded song, for contextual suggestions */
  songKey?: AnalysisKey | null;
  songStats?: AnalysisStatistics | null;
  songTitle?: string | null;
}

// ── Component ──────────────────────────────────────────────────

export const GeneratePanel = memo(function GeneratePanel({
  open,
  onClose,
  onSongLoaded,
  songKey,
  songStats,
  songTitle,
}: GeneratePanelProps) {
  const { studio } = useStudio();
  const modifierKey = useModifierKey();

  const [prompt, setPrompt] = useState("");
  const [bpm, setBpm] = useState(90);
  const [measures, setMeasures] = useState(4);
  const [status, setStatus] = useState<PanelStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [slowHint, setSlowHint] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const history = useGenerationHistory();

  const { isAuthenticated } = useAuth();
  const { openAccountModal } = useAccountModal();

  // Auto-focus textarea when panel opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => textareaRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Clean up slow hint timer
  useEffect(() => {
    return () => {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, []);

  const canGenerate = prompt.trim().length > 0 && status !== "generating";

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || !studio) return;

    // La route exige un compte depuis le 2026-08-26 : chaque generation est un
    // appel de modele facture, et le quota est indexe sur l utilisateur, donc
    // l anonyme n en avait aucun. On ouvre la modale plutot que de laisser
    // partir un appel dont on sait qu il reviendra en 401 : le prompt saisi
    // reste a l ecran, et la generation repart d un clic une fois le compte cree.
    if (!isAuthenticated) {
      openAccountModal("exercise_generate");

      return;
    }

    setStatus("generating");
    setError(null);
    setSlowHint(false);

    // Show hint after 3 seconds
    slowTimerRef.current = setTimeout(() => setSlowHint(true), 3000);

    try {
      const exercise = await generateExercise({
        prompt: prompt.trim(),
        bpm,
        measures,
      });

      clearTimeout(slowTimerRef.current);

      // Save to history
      history.add({
        prompt: prompt.trim(),
        bpm,
        measures,
        result: exercise.analysisData,
      });

      // Load into studio
      const song = createSongFromRawAnalysis(exercise.analysisData);
      const songControler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
      await songControler.load();
      studio.addTrack(songControler);
      onSongLoaded?.(songControler);

      // Brief success state
      setStatus("success");
      setTimeout(() => {
        setStatus("idle");
        setPrompt("");
        onClose();
      }, 600);
    } catch (err) {
      clearTimeout(slowTimerRef.current);
      setStatus("error");
      setError(
        err instanceof Error ? err.message : "Erreur lors de la generation",
      );
    }
  }, [
    canGenerate,
    studio,
    isAuthenticated,
    openAccountModal,
    prompt,
    bpm,
    measures,
    history,
    onSongLoaded,
    onClose,
  ]);

  const handleHistorySelect = useCallback(
    async (item: GenerationHistoryItem) => {
      if (!studio) return;

      try {
        const song = createSongFromRawAnalysis(item.result);
        const songControler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
        await songControler.load();
        studio.addTrack(songControler);
        onSongLoaded?.(songControler);
        onClose();
      } catch (err) {
        setError("Impossible de charger l'exercice depuis l'historique");
        setStatus("error");
      }
    },
    [studio, onSongLoaded, onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleGenerate();
      }
    },
    [handleGenerate],
  );

  // CTA button styles based on status
  const ctaBg =
    status === "success"
      ? "#22c55e"
      : canGenerate
        ? "#8b5cf6"
        : "rgba(139,92,246,0.12)";
  const ctaColor =
    status === "success" || canGenerate
      ? "#ffffff"
      : "rgba(167,139,250,0.4)";
  const ctaShadow =
    status === "success"
      ? "0 0 20px rgba(34,197,94,0.25)"
      : canGenerate
        ? "0 0 20px rgba(139,92,246,0.25), 0 4px 12px rgba(0,0,0,0.3)"
        : "none";

  return (
    <div
      className="h-full shrink-0 relative z-10 overflow-hidden transition-[width] duration-200 ease-out"
      style={{ width: open ? PANEL_WIDTH : 0 }}
    >
    <div
      className="h-full flex flex-col bg-bg-surface border-l border-border-subtle transition-transform duration-200 ease-out"
      style={{
        width: PANEL_WIDTH,
        transform: open ? "translateX(0)" : "translateX(100%)",
      }}
    >
      {/* Indeterminate loading bar */}
      {status === "generating" && (
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-10">
          <div
            className="h-full rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, #8b5cf6, transparent)",
              width: "40%",
              animation: "indeterminate 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 shrink-0 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-4 h-4 text-accent-bright" />
          <span className="text-sm font-semibold text-text-primary">
            Generer un exercice
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-white/[0.05] transition-colors cursor-pointer"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M4 4l6 6M10 4l-6 6" />
          </svg>
        </button>
      </div>

      {/* Scrollable content area */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-4"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(139,92,246,0.2) transparent",
        }}
      >
        {/* Prompt textarea */}
        <div className="flex flex-col gap-1.5">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              if (status === "error") {
                setError(null);
                setStatus("idle");
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Décris l'exercice que tu souhaites pratiquer..."
            readOnly={status === "generating"}
            className="w-full min-h-[88px] max-h-[160px] px-3 py-2.5 rounded-xl text-sm text-text-primary leading-relaxed placeholder:text-text-muted/50 resize-none outline-none transition-all duration-150"
            style={{
              backgroundColor: "rgba(255,255,255,0.03)",
              border:
                status === "error"
                  ? "1px solid rgba(239,68,68,0.4)"
                  : "1px solid rgba(255,255,255,0.06)",
            }}
            onFocus={(e) => {
              if (status !== "error") {
                e.currentTarget.style.borderColor = "rgba(139,92,246,0.35)";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.08)";
              }
            }}
            onBlur={(e) => {
              if (status !== "error") {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                e.currentTarget.style.boxShadow = "none";
              }
            }}
          />
          <div className="flex items-center justify-end">
            <span className="text-[10px] text-text-muted/40">
              {modifierKey}\u21B5 pour generer
            </span>
          </div>
        </div>

        {/* Suggestions */}
        <PromptSuggestions
          onSelect={(text) => {
            setPrompt(text);
            textareaRef.current?.focus();
          }}
          songKey={songKey}
          songStats={songStats}
          songTitle={songTitle}
        />

        {/* Divider */}
        <div className="h-px" style={{ backgroundColor: "rgba(255,255,255,0.04)" }} />

        {/* Parameters */}
        <GenerationParams
          bpm={bpm}
          measures={measures}
          onBpmChange={setBpm}
          onMeasuresChange={setMeasures}
        />

        {/* Divider before history */}
        {history.items.length > 0 && (
          <div className="h-px" style={{ backgroundColor: "rgba(255,255,255,0.04)" }} />
        )}

        {/* History */}
        <GenerationHistory
          items={history.items}
          onSelect={handleHistorySelect}
          onRemove={history.remove}
        />
      </div>

      {/* Sticky footer, CTA */}
      <div className="shrink-0 px-4 pb-4 pt-2 border-t border-border-subtle">
        {/* Error message */}
        {status === "error" && error && (
          <p className="text-[11px] text-err mb-2 px-1">{error}</p>
        )}

        {/* Slow generation hint */}
        {status === "generating" && slowHint && (
          <p className="text-[11px] text-text-muted mb-2 px-1">
            Cela peut prendre quelques secondes...
          </p>
        )}

        <button
          onClick={handleGenerate}
          disabled={!canGenerate && status !== "generating"}
          className="w-full h-10 rounded-xl text-[13px] font-semibold transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
          style={{
            backgroundColor: ctaBg,
            color: ctaColor,
            boxShadow: ctaShadow,
            pointerEvents: status === "generating" ? "none" : "auto",
          }}
        >
          {status === "generating" ? (
            <>
              <LoaderIcon className="w-4 h-4 animate-spin" />
              Generation en cours...
            </>
          ) : status === "success" ? (
            <>
              <CheckIcon className="w-4 h-4" />
              Exercice pret
            </>
          ) : (
            <>
              <SparklesIcon className="w-4 h-4" />
              Generer
            </>
          )}
        </button>

        <p className="text-[10px] text-text-muted/40 text-center mt-1.5">
          {modifierKey}\u21B5 pour generer
        </p>
      </div>
    </div>
    </div>
  );
});
